import './NonLiveReport.scss';
import * as Dfns from 'date-fns/fp';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Collapse, Table } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '../../redux';
import { currentCompanySelector } from '../../redux/company';
import { useSetError } from '../../redux/modals';
import { awaitJSON, StreamingLambdaFetch } from '../../utils/fetch-utils';
import {
  Placement,
  PlacementStatus,
  PlacementStatusType,
  STATUS_LABEL
} from './non-live-report-constants';
import { getRowInfo, PlacementWithStatus, sortPlacementsByStatus } from './non-live-report-utils';

const sectionKey = ({ network, platform, adServer }: Placement) =>
  `${network} ${platform} ${adServer}`;

interface PlacementSectionProps {
  placement: PlacementWithStatus;
  isOpen: boolean;
  onToggle: () => void;
}

const PlacementSection = ({ placement, isOpen, onToggle }: PlacementSectionProps) => {
  const { network, platform, adServer, status, count, iscis } = placement;
  return (
    <div className='placement-section'>
      <InteractiveDiv className='placement-section-header' onClick={onToggle}>
        <span
          className={cn('status-dot', `status-${status}`)}
          title={STATUS_LABEL[status]}
          aria-label={STATUS_LABEL[status]}
        />
        <span className='placement-section-title'>
          {`${adServer === 'ft' ? 'Flashtalking ' : ''}${network} ${platform} (${count.toLocaleString()} impressions received)`}
        </span>
        {isOpen ? <MdExpandLess /> : <MdExpandMore />}
      </InteractiveDiv>
      <Collapse in={isOpen}>
        <div>
          <Table bordered striped>
            <thead>
              <tr>
                <th>ISCI</th>
                <th>Count</th>
                <th>%</th>
                <th>Live?</th>
                <th>Source</th>
                <th>Ad Server</th>
              </tr>
            </thead>
            <tbody>
              {iscis.map(creative => {
                const { isci, count: creativeCount, campaign_source } = creative;
                const { liveText, liveClass, sourceClass } = getRowInfo(creative, count, adServer);
                return (
                  <tr key={isci}>
                    <td>{isci}</td>
                    <td>{creativeCount.toLocaleString()}</td>
                    <td>{Math.floor((creativeCount / count) * 10000) / 100}%</td>
                    <td className={liveClass}>{liveText}</td>
                    <td className={sourceClass}>{campaign_source}</td>
                    <td className={sourceClass}>{adServer ? adServer.toUpperCase() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Collapse>
    </div>
  );
};

interface ReportSummaryProps {
  total: number;
  redCount: number;
  yellowCount: number;
  greenCount: number;
}

const ReportSummary = ({ total, redCount, yellowCount, greenCount }: ReportSummaryProps) => {
  const segments = [
    `${total} placement${total === 1 ? '' : 's'}`,
    redCount > 0 && `${redCount} with issues`,
    yellowCount > 0 && `${yellowCount} minor`,
    greenCount > 0 && `${greenCount} all live`
  ].filter(Boolean);

  return <div className='report-summary'>{segments.join(' · ')}</div>;
};

export const NonLiveReport = () => {
  const setError = useSetError();
  const [creativeInfo, setCreativeInfo] = useState<PlacementWithStatus[]>();
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const company = useAppSelector(currentCompanySelector);

  useEffect(() => {
    let bail = false;
    if (company) {
      (async () => {
        try {
          const res = await StreamingLambdaFetch(`/check_creatives/${company}`);
          const data = await awaitJSON<Record<string, Placement[]>>(res);

          if (bail) return;

          if (!data[company]) {
            setCreativeInfo([]);
            return;
          }

          const sorted = sortPlacementsByStatus(data[company]);
          setCreativeInfo(sorted);
          // Smart default: expand sections that need attention, collapse the all-live ones.
          setOpenKeys(
            new Set(
              sorted.filter(placement => placement.status !== PlacementStatus.GREEN).map(sectionKey)
            )
          );
        } catch (e) {
          setError({
            message: `Couldn't run non-live creative check. Error: ${e.message}`,
            reportError: e
          });
        }
      })();
    }
    return () => {
      bail = true;
    };
  }, [company, setError]);

  const sections = useMemo(
    () => (creativeInfo ?? []).map(placement => ({ ...placement, key: sectionKey(placement) })),
    [creativeInfo]
  );

  const reportDate = Dfns.format('EEEE, LLLL do y')(Dfns.subDays(1)(new Date()));

  if (!creativeInfo) return <FullPageSpinner />;

  if (!sections.length)
    return (
      <div className='non-live-report-page'>
        <h3>Creative Report ({reportDate})</h3>
        <div>No impressions ran yesterday</div>
      </div>
    );

  const counts = sections.reduce<Record<PlacementStatusType, number>>(
    (acc, { status }) => ({ ...acc, [status]: acc[status] + 1 }),
    { [PlacementStatus.RED]: 0, [PlacementStatus.YELLOW]: 0, [PlacementStatus.GREEN]: 0 }
  );

  const allOpen = sections.every(({ key }) => openKeys.has(key));

  const toggleAll = () =>
    setOpenKeys(allOpen ? new Set() : new Set(sections.map(({ key }) => key)));

  const toggleSection = (key: string) =>
    setOpenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <div className='non-live-report-page'>
      <div className='report-header'>
        <div className='report-header-info'>
          <h3>Creative Report ({reportDate})</h3>
          <ReportSummary
            total={sections.length}
            redCount={counts[PlacementStatus.RED]}
            yellowCount={counts[PlacementStatus.YELLOW]}
            greenCount={counts[PlacementStatus.GREEN]}
          />
        </div>
        <Button appearance={Appearance.OUTLINED} size={Size.SM} onClick={toggleAll}>
          {allOpen ? 'Collapse all' : 'Expand all'}
        </Button>
      </div>
      {sections.map(section => (
        <PlacementSection
          key={section.key}
          placement={section}
          isOpen={openKeys.has(section.key)}
          onToggle={() => toggleSection(section.key)}
        />
      ))}
    </div>
  );
};
